import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Keyboard, TouchableWithoutFeedback, Animated, Platform } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { 
  BaseAdminScreen,
  AdminEmptyScreenLayout,
  LoadingSpinner,
  InlineEmptyMessage,
} from '@/components';
import { SearchBar } from '@/components/common/admin/mobile/SearchBar';
import { ConversationList } from '@/components/admin/messages/ConversationList';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { useConversations, useMessageFilters } from '@/hooks/admin/useMessages';
import { FilterType, FILTER_VALUES } from '@/types/admin/messages';
import { ADMIN_COLORS, EMPTY_MESSAGES } from '@/constants/admin';
import { getFilterEmptyState, getNoResultsState } from '@/utils/admin/messages/emptyState';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopMessagesView } from './desktop/DesktopMessagesView';
import { useDebouncedValue } from '@/hooks/common/useDebouncedValue';
import { DataTableProvider } from '@/contexts/admin/DataTableContext';
import { useAppSelector } from '@/store';
import { selectIsMenuOpen } from '@/store/selectors/adminUISelectors';

type MessagesScreenProps = StackScreenProps<AdminStackParamList, 'Messages'>;

const DATATABLE_DEFAULTS = { paginationModel: { page: 0, pageSize: 25 } };

export const MessagesScreen: React.FC<MessagesScreenProps> = ({
  navigation,
}) => {
  const { layoutMode } = useResponsive();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 250);
  
  // Get navigation menu state to close sort menu when nav opens
  const isNavigationMenuOpen = useAppSelector(selectIsMenuOpen);

  const { 
    conversations, 
    isLoading, 
    error, 
    setConversationQuery,
    filterType,
    setFilterType,
    sortType,
    setSortType,
    hasNextPage,
    loadMore,
  } = useConversations();
  const { filterOptions, sortOptions } = useMessageFilters();
  const [showSortMenu, setShowSortMenu] = useState(false);

  // Remove debug logs; noop effect preserved intentionally if future side-effects are needed
  useEffect(() => {}, [conversations]);
  
  // Update search query when it changes
  React.useEffect(() => {
    setConversationQuery(debouncedSearch);
  }, [debouncedSearch]); // Remove setConversationQuery from dependencies
  
  // Close sort menu when navigation menu opens (mobile native only)
  useEffect(() => {
    if (isNavigationMenuOpen && Platform.OS !== 'web') {
      setShowSortMenu(false);
    }
  }, [isNavigationMenuOpen]);
  
  // Determine if we have data to show in the list
  const hasData = conversations.length > 0;
  const hasSearchQuery = searchQuery.trim() !== '';
  
  // Show loading state if we're loading and have no data yet and no active search
  const showLoadingState = isLoading && !hasData && !hasSearchQuery;
  
  // Show error state if we have a critical error and no data and no active search
  const showErrorState = error && !hasData && !isLoading && !hasSearchQuery;

  // Handle conversation selection
  const handleConversationSelect = useCallback((conversationId: number) => {
    navigation.navigate('Chat', { conversationId });
  }, [navigation]);

  // Handle create conversation
  const handleCreateConversation = useCallback(() => {
    navigation.navigate('CreateMessage');
  }, [navigation]);



  // Render filter tabs
  const renderFilterTabs = () => (
    <View style={styles.filterContainer}>
      {filterOptions.map((option) => {
        const isSelected = FILTER_VALUES[filterType] === option;
        return (
          <TouchableOpacity
            key={option}
            style={[
              styles.filterTab,
              isSelected && styles.selectedFilterTab,
            ]}
            onPress={() => {
              const filterKey = Object.keys(FILTER_VALUES).find(
                key => FILTER_VALUES[key as FilterType] === option
              ) as FilterType;
              setFilterType(filterKey);
            }}
          >
            <Text
              style={[
                styles.filterTabText,
                isSelected && styles.selectedFilterTabText,
              ]}
            >
              {option}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );

  // Render sort toggle
  const renderSortToggle = () => {
    const currentSortOption = sortOptions.find(opt => opt.key === sortType);
    
    return (
      <View style={styles.sortContainer}>
        <TouchableOpacity
          style={styles.sortToggle}
          onPress={() => setShowSortMenu(!showSortMenu)}
          activeOpacity={0.7}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <Text style={styles.sortToggleText}>
            {currentSortOption?.value || 'Sort by latest'}
          </Text>
          <View style={[
            styles.chevronContainer,
            showSortMenu && styles.chevronContainerRotated,
          ]}>
            <SVG_COMPONENTS.CHEVRON_DOWN
              width={16}
              height={16}
              color={ADMIN_COLORS.TEXT_SECONDARY}
            />
          </View>
        </TouchableOpacity>

        {showSortMenu && (
          <View style={styles.sortMenu}>
            {sortOptions.map((option) => (
              <TouchableOpacity
                key={option.key}
                style={styles.sortMenuItem}
                onPress={() => {
                  setSortType(option.key);
                  setShowSortMenu(false);
                }}
                activeOpacity={0.7}
              >
                <Text style={styles.sortMenuItemText}>
                  {option.value}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </View>
    );
  };

  // Render empty state message for when no data but tabs should remain visible
  const renderEmptyMessage = () => {
    const cfg = getFilterEmptyState(filterType);
    return (
      <InlineEmptyMessage
        title={cfg.title}
        message={cfg.message}
        icon={cfg.icon}
        containerStyle={styles.emptyMessageContainer}
        titleStyle={styles.emptyMessageText}
        messageStyle={styles.emptyMessageSubtext}
      />
    );
  };

  // Render error state
  const renderErrorState = () => (
    <View style={styles.errorContainer}>
      <SVG_COMPONENTS.X_CIRCLE width={48} height={48} color={ADMIN_COLORS.ERROR} />
      <Text style={styles.errorTitle}>Unable to Load Conversations</Text>
      <Text style={styles.errorMessage}>
        Something went wrong while loading the conversations.
      </Text>
    </View>
  );

  // Render content based on state
  const renderContent = () => {
    if (showLoadingState) {
      return <LoadingSpinner message="Loading conversations..." />;
    }
    
    if (showErrorState) {
      return renderErrorState();
    }

    return (
      <View style={styles.container}>
        {/* Header with search */}
        <View style={styles.header}>
          <SearchBar
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Search conversations..."
            style={styles.searchBar}
          />
        </View>

        {/* Filter tabs - always visible */}
        {renderFilterTabs()}
        
        {/* Sort toggle - always visible */}
        {renderSortToggle()}

        {/* Main content */}
        <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
          <View style={styles.content}>
            {hasData ? (
              <ConversationList
                conversations={conversations}
                hasNextPage={hasNextPage}
                isLoading={isLoading}
                loadMore={loadMore}
                onConversationSelect={handleConversationSelect}
              />
            ) : hasSearchQuery ? (
              (() => { const cfg = getNoResultsState(searchQuery); return (
                <InlineEmptyMessage
                  title={cfg.title}
                  message={cfg.message}
                  icon={cfg.icon}
                  containerStyle={styles.noResultsContainer}
                  titleStyle={styles.noResultsTitle}
                  messageStyle={styles.noResultsMessage}
                />);
              })()
            ) : (
              renderEmptyMessage()
            )}
          </View>
        </TouchableWithoutFeedback>
      </View>
    );
  };

  return (
    layoutMode === 'desktop' ? (
      <AdminErrorBoundary context="MessagesScreenDesktop">
        <DesktopMessagesView />
      </AdminErrorBoundary>
    ) : (
      <AdminErrorBoundary context="MessagesScreen">
        <DataTableProvider tableKey="admin:messages" defaults={DATATABLE_DEFAULTS}>
          <BaseAdminScreen
            screenName="Messages"
            navigation={navigation}
            showPlaceholder={false}
            scrollEnabled={false}
            contentContainerStyle={styles.baseScreenContent}
          >
            <AdminEmptyScreenLayout
              title="Messages"
              emptyStateType="messages"
              onAddPress={handleCreateConversation}
              showAddButton={true}
              buttonType="add"
              hasContent={true}
              disableScrollView={true}
            >
              {renderContent()}
            </AdminEmptyScreenLayout>
          </BaseAdminScreen>
        </DataTableProvider>
      </AdminErrorBoundary>
    )
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    flexGrow: 1,
  },
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
    minHeight: 70,
  },
  searchBar: {
    flex: 1,
  },
  content: {
    flex: 1,
  },

  // Filter tabs styles
  filterContainer: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  filterTab: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginHorizontal: 4,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    alignItems: 'center',
  },
  selectedFilterTab: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  filterTabText: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  selectedFilterTabText: {
    color: '#FFFFFF',
  },

  // Sort toggle styles
  sortContainer: {
    position: 'relative',
    paddingHorizontal: 16,
    paddingBottom: 8,
    // Use high z-index for web, lower for native to avoid navigation menu overlap
    zIndex: Platform.OS === 'web' ? 1001 : 100,
  },
  sortToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    minHeight: 44, // Ensure proper touch target size
  },
  sortToggleText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginRight: 8,
  },
  chevronContainer: {
    transform: [{ rotate: '0deg' }],
  },
  chevronContainerRotated: {
    transform: [{ rotate: '180deg' }],
  },
  sortMenu: {
    position: 'absolute',
    top: 40,
    left: 16,
    right: 16,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    zIndex: 1000,
  },
  sortMenuItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  sortMenuItemText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },

  // Error state styles
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  errorMessage: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 20,
    maxWidth: 300,
  },

  // Empty message styles (when tabs are visible but no data)
  emptyMessageContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: -60,
    paddingHorizontal: 24,
  },
  emptyMessageIcon: {
    marginBottom: 16,
  },
  emptyMessageText: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    textAlign: 'center',
    marginBottom: 8,
  },
  emptyMessageSubtext: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 300,
  },


  noResultsContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: -120,
    paddingHorizontal: 20,
  },
  noResultsTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 8,
    textAlign: 'center',
  },
  noResultsMessage: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 300,
  },
});